import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Package, FileText, ShoppingCart, TrendingUp, ArrowRight, PlusCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getProducts, getPosts, getSettings } from '@/lib/store';

export function AdminDashboard() {
  const products = useMemo(() => getProducts(), []);
  const posts = useMemo(() => getPosts(), []);
  const settings = getSettings();

  const featured = products.filter((p) => p.featured);
  const inStock = products.filter((p) => p.inStock);
  const published = posts.filter((p) => p.published);

  const stats = [
    { label: 'Total Products', value: products.length, sub: `${inStock.length} in stock`, icon: Package, color: 'text-amber-500', bg: 'bg-amber-500/10' },
    { label: 'Blog Posts', value: posts.length, sub: `${published.length} published`, icon: FileText, color: 'text-blue-400', bg: 'bg-blue-400/10' },
    { label: 'Featured Items', value: featured.length, sub: 'on homepage', icon: TrendingUp, color: 'text-green-400', bg: 'bg-green-400/10' },
    { label: 'Payment Provider', value: settings.checkoutProvider === 'both' ? 'PayPal + Square' : settings.checkoutProvider === 'paypal' ? 'PayPal' : 'Square', sub: settings.paypalClientId || settings.squareAppId ? 'configured' : 'not configured', icon: ShoppingCart, color: 'text-purple-400', bg: 'bg-purple-400/10' },
  ];

  const recentProducts = [...products].sort((a, b) => b.createdAt - a.createdAt).slice(0, 5);
  const recentPosts = [...posts].sort((a, b) => b.createdAt - a.createdAt).slice(0, 3);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-stone-400 text-sm mt-1">Welcome back to the Dakota Horizon admin area.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(({ label, value, sub, icon: Icon, color, bg }) => (
          <Card key={label} className="bg-[hsl(25,20%,12%)] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-stone-400 text-xs mb-1">{label}</p>
                  <p className={`text-2xl font-bold ${color}`}>{value}</p>
                  <p className="text-stone-500 text-xs mt-0.5">{sub}</p>
                </div>
                <div className={`${bg} p-2 rounded-md`}>
                  <Icon className={`h-4 w-4 ${color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Products */}
        <Card className="bg-[hsl(25,20%,12%)] border-white/10">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-white text-base font-semibold">Recent Products</CardTitle>
            <div className="flex gap-2">
              <Button asChild size="sm" variant="ghost" className="text-stone-400 hover:text-white h-7 px-2">
                <Link to="/admin/products/new"><PlusCircle className="h-4 w-4 mr-1" />Add</Link>
              </Button>
              <Button asChild size="sm" variant="ghost" className="text-stone-400 hover:text-white h-7 px-2">
                <Link to="/admin/products"><ArrowRight className="h-4 w-4" /></Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3">
              {recentProducts.map((p) => (
                <div key={p.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <div>
                    <p className="text-stone-200 text-sm font-medium leading-tight">{p.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-stone-500 text-xs capitalize">{p.category}</span>
                      {!p.inStock && <Badge className="bg-red-900/40 text-red-400 text-[10px] px-1.5 py-0 border-0">Out of Stock</Badge>}
                    </div>
                  </div>
                  <span className="text-amber-400 font-semibold text-sm">${p.price.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Blog Posts */}
        <Card className="bg-[hsl(25,20%,12%)] border-white/10">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-white text-base font-semibold">Recent Blog Posts</CardTitle>
            <div className="flex gap-2">
              <Button asChild size="sm" variant="ghost" className="text-stone-400 hover:text-white h-7 px-2">
                <Link to="/admin/blog/new"><PlusCircle className="h-4 w-4 mr-1" />Add</Link>
              </Button>
              <Button asChild size="sm" variant="ghost" className="text-stone-400 hover:text-white h-7 px-2">
                <Link to="/admin/blog"><ArrowRight className="h-4 w-4" /></Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3">
              {recentPosts.map((post) => (
                <div key={post.id} className="py-2 border-b border-white/5 last:border-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-stone-200 text-sm font-medium leading-tight line-clamp-2">{post.title}</p>
                    <Badge className={post.published
                      ? 'bg-green-900/40 text-green-400 border-0 text-[10px] px-1.5 shrink-0'
                      : 'bg-stone-800 text-stone-400 border-0 text-[10px] px-1.5 shrink-0'
                    }>
                      {post.published ? 'Live' : 'Draft'}
                    </Badge>
                  </div>
                  <p className="text-stone-500 text-xs mt-0.5">{post.author}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick actions */}
      {(!settings.paypalClientId && !settings.squareAppId) && (
        <Card className="bg-amber-900/20 border-amber-700/30">
          <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <ShoppingCart className="h-5 w-5 text-amber-400 shrink-0" />
            <div className="flex-1">
              <p className="text-amber-300 font-medium text-sm">Payment provider not configured</p>
              <p className="text-stone-400 text-xs mt-0.5">Add your PayPal Client ID or Square credentials to enable checkout.</p>
            </div>
            <Button asChild size="sm" className="bg-amber-600 hover:bg-amber-500 text-white rounded-sm shrink-0">
              <Link to="/admin/settings">Configure Now</Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default AdminDashboard;
